import { memo, useEffect } from 'react'

import type { Platform } from '../types'

import { createBottomLift } from './bottomLift'
import { computeViewportVars, type ViewportVars } from './computeViewportVars'
import { emitKeyboardSettled } from './keyboardChannel'

type ViewportRuntimeProps = {
  platform: Platform
}

/** Типы `<input>`, которые экранную клавиатуру не открывают. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
])

/** Открыта ли клавиатура, решает не размер вьюпорта, а сфокусированный элемент. */
function isTextInputFocused(): boolean {
  const element = document.activeElement

  if (element instanceof HTMLTextAreaElement) return true
  if (element instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(element.type)
  return element instanceof HTMLElement && element.isContentEditable
}

/**
 * Единственное место, откуда раскладка узнаёт размеры окна: публикует на `<html>`
 * `--tg-viewport-stable-height`, `--tg-viewport-bottom-inset`, `--tg-viewport-offset-top`,
 * `--tg-keyboard-inset`, `--tg-safe-area-top/-bottom`, `--tg-content-safe-area-top`,
 * `--layout-bottom-lift` и пару шторки `--layout-sheet-lift` / `--layout-sheet-room`.
 * Значения по умолчанию лежат в `shared/styles/global.scss` и работают до первого расчёта
 * и вообще вне Telegram.
 *
 * Почему высоту меряем сами, а не берём `viewportHeight` из Telegram: на iOS он не замечает
 * экранную клавиатуру и возвращает одно и то же значение (Telegram-iOS#1296). Единственный
 * источник, который её видит на всех платформах, — `visualViewport`.
 *
 * Почему при этом не обходимся одним `clientHeight`: он знает про окно вебвью, но не про то,
 * какая его часть видна. Соотношение двух источников — в `bottomLift.ts`.
 *
 * Замер и его публикация разведены: пока клавиатура едет, iOS отдаёт несколько разных чисел,
 * и первое из них завышено. Наружу уходит только устоявшаяся величина подъёма, и она никогда
 * не больше того, что скрыто снизу прямо сейчас, — вся эта арифметика в `bottomLift.ts`.
 *
 * Ширину окна не публикуем: приложение и так живёт в колонке `--app-max-width`.
 */
export const ViewportRuntime = memo(function ViewportRuntime(props: ViewportRuntimeProps) {
  const { platform } = props

  useEffect(() => {
    const root = document.documentElement
    const visual = window.visualViewport
    const resolveLift = createBottomLift()
    let published: ViewportVars = {}

    const publish = () => {
      const { safeArea, contentSafeArea, height } = platform.viewport
      // clientHeight — высота именно окна вебвью. Запасные варианты нужны только для
      // окружений без раскладки (тесты, SSR), где clientHeight равен нулю.
      const layoutHeight = root.clientHeight || height || window.innerHeight

      // Ни одна из высот тут не «придерживается»: все три замера берутся заново каждый кадр.
      // Держать высоту окна и пересчитывать от неё клавиатуру уже пробовали — подъём выходил
      // равным клавиатуре плюс полосе, которую Telegram успел забрать себе, и кнопка уезжала
      // под потолок на всё время фокуса (docs/telegram.md §5.3).
      const state = resolveLift(
        {
          layoutHeight,
          layoutWidth: root.clientWidth || window.innerWidth,
          reportedStableHeight: height,
          visualHeight: visual?.height ?? layoutHeight,
          visualOffsetTop: visual?.offsetTop ?? 0,
          isTextInputFocused: isTextInputFocused(),
        },
        performance.now(),
      )

      const next = computeViewportVars({
        layoutHeight,
        band: state.band,
        lift: state.lift,
        sheetLift: state.sheetLift,
        sheetRoom: state.sheetRoom,
        offsetTop: state.offsetTop,
        safeArea,
        contentSafeArea,
      })

      for (const [name, value] of Object.entries(next)) {
        if (published[name] !== value) root.style.setProperty(name, value)
      }
      published = next

      // Замер ещё не устоялся — событий может больше и не быть (iOS шлёт их не каждый кадр),
      // поэтому следующий замер заказываем сами.
      if (state.isPending) schedule()
      if (state.hasChanged) emitKeyboardSettled()
    }

    // Пересчёт откладываем на кадр: и Telegram, и iOS шлют событие раньше, чем доводят
    // размеры до конца, а во время анимации клавиатуры событий прилетает пачка — так из неё
    // остаётся один расчёт вместо десятка. Тем же кадром себя переспрашивает и `bottomLift`,
    // пока клавиатура едет. Движение анимирует CSS (`--layout-bottom-lift`, см. global.scss).
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(publish)
    }

    publish()

    const unsubscribePlatform = platform.onViewportChanged(schedule)
    visual?.addEventListener('resize', schedule)
    // scroll — это и есть сдвиг видимой полосы к полю ввода, без него мы не узнаем offsetTop.
    visual?.addEventListener('scroll', schedule)
    window.addEventListener('resize', schedule)
    // Фокус — это и есть настоящее событие «клавиатура открывается / закрывается». Размеры
    // приходят с опозданием: на закрытии iOS шлёт `resize` уже ПОСЛЕ анимации, и без этих
    // двух подписок закреплённая кнопка успевала повисеть на месте уехавшей клавиатуры.
    // `focusout` обрабатывается на следующем кадре, когда `document.activeElement` уже
    // обновился, — это и делает `schedule`.
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)

    return () => {
      cancelAnimationFrame(frame)
      unsubscribePlatform()
      visual?.removeEventListener('resize', schedule)
      visual?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
      for (const name of Object.keys(published)) root.style.removeProperty(name)
    }
  }, [platform])

  return null
})
